import { TZDate } from "@date-fns/tz";
import { endOfDay, format, formatISO, parseISO, startOfDay } from "date-fns";

/**
 * Gets the user's local timezone from the browser
 */
export const getUserTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Converts a local date to UTC ISO string
 * @param date Local date object or ISO string
 * @param timezone User's timezone (defaults to browser timezone)
 */
export const localToUTC = (date: Date | string, timezone: string = getUserTimezone()): string => {
  const localDate = typeof date === "string" ? parseISO(date) : date;
  // Create a TZDate in the specified timezone
  const tzDate = new TZDate(localDate.getTime(), timezone);
  // Convert to UTC
  return formatISO(new Date(tzDate.getTime()));
};

/**
 * Converts a UTC date to local date
 * @param date UTC date object or ISO string
 * @param timezone User's timezone (defaults to browser timezone)
 */
export const utcToLocal = (date: Date | string, timezone: string = getUserTimezone()): Date => {
  const utcDate = typeof date === "string" ? parseISO(date) : date;
  // Create a TZDate in UTC
  const utcTzDate = new TZDate(utcDate.getTime(), "UTC");
  // Convert to the target timezone
  const localTzDate = utcTzDate.withTimeZone(timezone);
  return new Date(localTzDate.getTime());
};

/**
 * Formats a UTC date to local date string with the specified format
 * @param date UTC date object or ISO string
 * @param formatStr Format string for date-fns
 * @param timezone User's timezone (defaults to browser timezone)
 */
export const formatUTCToLocal = (
  date: Date | string,
  formatStr: string = "yyyy-MM-dd HH:mm:ss",
  timezone: string = getUserTimezone()
): string => {
  const localDate = utcToLocal(date, timezone);
  return format(localDate, formatStr);
};

/**
 * Gets the start of day in UTC for a local date
 * @param date Local date object or ISO string
 * @param timezone User's timezone (defaults to browser timezone)
 */
export const startOfLocalDayInUTC = (date: Date | string, timezone: string = getUserTimezone()): string => {
  const localDate = typeof date === "string" ? parseISO(date) : date;
  // Create a TZDate in the specified timezone
  const tzDate = new TZDate(localDate.getTime(), timezone);
  // Get start of day in that timezone
  const startOfLocalDay = startOfDay(new Date(tzDate.getTime()));
  // Convert back to TZDate to ensure timezone is preserved
  const startOfDayTz = new TZDate(startOfLocalDay.getTime(), timezone);
  // Convert to UTC
  return formatISO(new Date(startOfDayTz.getTime()));
};

/**
 * Gets the end of day in UTC for a local date
 * @param date Local date object or ISO string
 * @param timezone User's timezone (defaults to browser timezone)
 */
export const endOfLocalDayInUTC = (date: Date | string, timezone: string = getUserTimezone()): string => {
  const localDate = typeof date === "string" ? parseISO(date) : date;
  // Create a TZDate in the specified timezone
  const tzDate = new TZDate(localDate.getTime(), timezone);
  // Get end of day in that timezone
  const endOfLocalDay = endOfDay(new Date(tzDate.getTime()));
  // Convert back to TZDate to ensure timezone is preserved
  const endOfDayTz = new TZDate(endOfLocalDay.getTime(), timezone);
  // Convert to UTC
  return formatISO(new Date(endOfDayTz.getTime()));
};
